import React from 'react';
import { Link, Navigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowRight, Landmark, CheckCircle2, Calculator } from 'lucide-react';
import Seo from '../components/Seo';
import LandingHeader from '../components/LandingHeader';
import TaxCalculator from './TaxCalculator';
import RelatedLinks from '../components/RelatedLinks';
import ProductBridge from '../components/ProductBridge';
import PageGuide from '../components/PageGuide';
import { STATE_PAGES, STATE_LIST } from '../data/statePages';

export default function StateTaxLanding({ slug }) {
  const data = STATE_PAGES[slug];
  if (!data) return <Navigate to="/tools/1099-tax-calculator" replace />;

  const path = `/${data.slug}`;
  const others = STATE_LIST.filter((p) => p.slug !== data.slug);
  const jsonLd = [
    { '@context': 'https://schema.org', '@type': 'WebPage', name: data.h1, url: `https://moneyfeed.io${path}`, description: data.seoDescription },
    { '@context': 'https://schema.org', '@type': 'FAQPage', mainEntity: data.faqs.map((f) => ({ '@type': 'Question', name: f.q, acceptedAnswer: { '@type': 'Answer', text: f.a } })) },
    { '@context': 'https://schema.org', '@type': 'BreadcrumbList', itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://moneyfeed.io/' },
      { '@type': 'ListItem', position: 2, name: data.h1, item: `https://moneyfeed.io${path}` },
    ] },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#F8FAFC] via-[#ECFDF5] to-[#D1FAE5]" data-testid={`state-tax-landing-${data.slug}`}>
      <Seo title={data.seoTitle} description={data.seoDescription} path={path} jsonLd={jsonLd} />
      <LandingHeader />

      <section className="max-w-4xl mx-auto px-4 sm:px-8 pt-14 pb-8 text-center">
        <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full mb-5" style={{ backgroundColor: `${data.accent}1A` }}>
          <Landmark className="h-4 w-4" style={{ color: data.accent }} />
          <span className="text-xs font-bold uppercase tracking-wider" style={{ color: data.accent }}>
            {data.noStateTax ? 'No state income tax' : `State tax ${data.rate}`}
          </span>
        </div>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-[#064E3B] leading-tight mb-5">
          Self-employment tax in <span style={{ color: data.accent }}>{data.hi}</span>
        </h1>
        <p className="text-base sm:text-lg text-gray-600 mb-8 max-w-2xl mx-auto">{data.intro}</p>
      </section>

      {/* What you owe */}
      <section className="max-w-3xl mx-auto px-4 sm:px-8 pb-6">
        <div className="bg-white rounded-3xl shadow-md p-6 sm:p-8 border border-gray-100">
          <h2 className="text-xl font-bold text-[#064E3B] mb-4">What a {data.state} freelancer owes</h2>
          <ul className="space-y-3">
            {data.points.map((p, i) => (
              <li key={i} className="flex gap-3 text-gray-700" data-testid={`state-point-${i}`}>
                <CheckCircle2 className="h-5 w-5 flex-shrink-0 mt-0.5" style={{ color: data.accent }} />
                <span className="text-sm sm:text-base">{p}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Embedded calculator */}
      <section className="max-w-5xl mx-auto px-4 sm:px-8 py-8">
        <div className="bg-white rounded-3xl shadow-md p-4 sm:p-6 border border-gray-100">
          <h2 className="text-2xl font-bold text-[#064E3B] text-center mb-1">Estimate your {data.state} taxes</h2>
          <p className="text-sm text-gray-500 text-center mb-5">Free 1099 self-employment tax calculator{data.noStateTax ? '' : ` — add ${data.state}'s ${data.rate} state rate`}. No sign-up.</p>
          <TaxCalculator initialRegion="US" embedded calcNote={data.noStateTax ? `${data.state} has no state income tax, so this federal estimate is your full bill.` : `Enter ${data.state}'s ${data.rate} in the State Tax field for a full estimate.`} />
        </div>
      </section>

      <PageGuide />

      {/* FAQ */}
      <section className="max-w-3xl mx-auto px-4 sm:px-8 py-8">
        <h2 className="text-2xl font-bold text-[#064E3B] text-center mb-8">{data.state} freelancer tax — FAQ</h2>
        <div className="space-y-4">
          {data.faqs.map((f, i) => (
            <div key={i} className="bg-white rounded-2xl shadow-sm p-6" data-testid={`state-faq-${i}`}>
              <h3 className="font-bold text-[#064E3B] mb-2">{f.q}</h3>
              <p className="text-gray-600 text-sm">{f.a}</p>
            </div>
          ))}
        </div>
        <p className="text-xs text-gray-400 text-center mt-6">Figures are 2026 headline guidance for freelancers and the self-employed, not tax advice. Check current IRS and state rules or a CPA for your situation.</p>
      </section>

      {/* Other states */}
      <section className="max-w-4xl mx-auto px-4 sm:px-8 py-8">
        <h2 className="text-lg font-bold text-[#064E3B] mb-4 text-center">Self-employment tax in other states</h2>
        <div className="flex flex-wrap justify-center gap-2">
          {others.map((o) => (
            <Link key={o.slug} to={`/${o.slug}`} className="px-4 py-2 bg-white rounded-full text-sm font-semibold text-[#064E3B] shadow-sm border border-gray-200 hover:border-[#84CC16]" data-testid={`state-link-${o.slug}`}>
              {o.state}
            </Link>
          ))}
          <Link to="/tools/1099-tax-calculator" className="px-4 py-2 bg-white rounded-full text-sm font-semibold text-[#064E3B] shadow-sm border border-gray-200 hover:border-[#84CC16]">
            1099 Tax Calculator
          </Link>
        </div>
      </section>

      <ProductBridge intent="tax" />

      {["self-employment-tax-new-york","self-employment-tax-washington","self-employment-tax-alaska","self-employment-tax-arkansas","self-employment-tax-connecticut","self-employment-tax-delaware","self-employment-tax-hawaii","self-employment-tax-idaho","self-employment-tax-iowa","self-employment-tax-kansas","self-employment-tax-kentucky","self-employment-tax-maine","self-employment-tax-mississippi","self-employment-tax-montana","self-employment-tax-nebraska","self-employment-tax-nevada","self-employment-tax-new-hampshire","self-employment-tax-new-mexico","self-employment-tax-north-dakota","self-employment-tax-rhode-island","self-employment-tax-south-dakota","self-employment-tax-vermont","self-employment-tax-west-virginia","self-employment-tax-wyoming"].includes(slug) && (<section className="max-w-4xl mx-auto px-4 sm:px-8 pb-8"><div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 text-center"><p className="text-sm font-semibold text-[#064E3B]">Track expenses automatically</p><p className="text-lg font-bold text-gray-900 mt-1">Meet MoneyFeed Scan Center</p><p className="text-gray-600 text-sm mt-2">Snap or send receipts on WhatsApp or in the app and MoneyFeed reads, categorizes and files them into clean books.</p><div className="flex flex-col sm:flex-row items-center justify-center gap-3 mt-4"><Link to="/scan-center" className="inline-block text-sm font-semibold text-[#0D9488]">Explore Scan Center →</Link><Link to="/auth" className="inline-block text-sm font-semibold text-[#064E3B]">Get started free →</Link></div></div></section>)}<RelatedLinks />

      <footer className="max-w-6xl mx-auto px-4 sm:px-8 py-8 text-center">
        <div className="flex flex-wrap justify-center gap-6 text-sm text-gray-500">
          <Link to="/" className="hover:text-[#064E3B]">Home</Link>
          <Link to="/whatsapp-bookkeeping" className="hover:text-[#064E3B]">WhatsApp Bookkeeping</Link>
          <Link to="/blog" className="hover:text-[#064E3B]">Blog</Link>
          <Link to="/auth" className="hover:text-[#064E3B]">Sign in</Link>
        </div>
      </footer>
    </div>
  );
}
